import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DatePipe, NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { NotificationsService } from './notifications.service';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { Subject, takeUntil, interval } from 'rxjs';

@Component({
    selector       : 'notifications',
    templateUrl    : './notifications.component.html',
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs       : 'notifications',
    standalone     : true,
    imports        : [MatButtonModule, NgIf, MatIconModule, MatTooltipModule, NgFor, NgClass, NgTemplateOutlet, RouterLink, DatePipe],
})
export class NotificationsComponent implements OnInit, OnDestroy
{
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel') private _notificationsPanel: TemplateRef<any>;

    notifications: Notification[] = [];
    unreadCount: number = 0;
    notificationFilter: 'all' | 'unread' | 'read' | 'new' | 'deleted' = 'all';

    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _notificationsService: NotificationsService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _router: Router,
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to notification changes
        this._notificationsService.notifications$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((notifications: Notification[]) =>
            {
                this.notifications = notifications;
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to unread count changes
        this._notificationsService.unreadCount$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((unreadCount: number) =>
            {
                console.log('Unread count in Component:', unreadCount);
                this.unreadCount = unreadCount;
                this._changeDetectorRef.markForCheck();
            });

        // Initial fetch
        this.loadNotifications();
        this._notificationsService.refreshUnreadCount();
        // Rafraîchissement périodique
        interval(45000)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() =>
            {
                this._notificationsService.getNotifications('unread').subscribe();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if ( this._overlayRef )
        {
            this._overlayRef.dispose();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    setFilter(filter: 'all' | 'unread' | 'read' | 'new' | 'deleted'): void {
        const validValues = ['all', 'unread', 'read', 'new', 'deleted'];
        this.notificationFilter = validValues.includes(filter) ? filter : 'all';
        this.loadNotifications();
    }

    loadNotifications(): void {
        this._notificationsService.getNotifications(this.notificationFilter).subscribe();
    }

    openPanel(): void
    {
        // Return if the notifications panel or its origin is not defined
        if ( !this._notificationsPanel || !this._notificationsOrigin )
        {
            return;
        }

        // Create the overlay if it doesn't exist
        if ( !this._overlayRef )
        {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(new TemplatePortal(this._notificationsPanel, this._viewContainerRef));
    }

    /**
     * Close the notifications panel
     */
    closePanel(): void
    {
        this._overlayRef.detach();
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): void
    {
        if (!this.notifications?.length) return;

        const unreadNotifications = this.notifications.filter(n => !n.read && !n.is_read);
        if (unreadNotifications.length === 0) return;

        const ids = unreadNotifications.map(n => n.id);

        this._notificationsService.markAsRead(ids).subscribe({
            next: () => {
                if (this.notificationFilter === 'unread') {
                    setTimeout(() => {
                        this.loadNotifications();
                    }, 500);
                }
                this._notificationsService.refreshUnreadCount();
            },
            error: (error) => {
                console.error('Error marking all as read:', error);
            }
        });
    }

    /**
     * CORRECTION : Mark all notifications as unread
     */
    markAllAsUnread(): void
    {
        if (!this.notifications?.length) return;

        const readNotifications = this.notifications.filter(n => n.read || n.is_read);
        if (readNotifications.length === 0) return;
        const ids = readNotifications.map(n => n.id);
        this._notificationsService.markAsUnread(ids).subscribe({
            next: () => {
                if (this.notificationFilter === 'read') {
                    setTimeout(() => {
                        this.loadNotifications();
                    }, 500);
                }
                this._notificationsService.refreshUnreadCount();
            },
            error: (error) => {
                console.error('Error marking all as unread:', error);
            }
        });
    }

    /**
     * Toggle notifications read/unread
     */
    toggleRead(notification: Notification): void
    {
        const isCurrentlyRead = notification.read || notification.is_read;

        if (isCurrentlyRead) {
            this._notificationsService.markAsUnread([notification.id]).subscribe({
                next: () => {
                    if (this.notificationFilter === 'read') {
                        setTimeout(() => {
                            this.loadNotifications();
                        }, 500);
                    }
                    this._notificationsService.refreshUnreadCount();
                },
                error: (error) => {
                    console.error('Error marking as unread:', error);
                }
            });
        } else {
            // Marquer comme lu
            this._notificationsService.markAsRead([notification.id]).subscribe({
                next: () => {
                    if (this.notificationFilter === 'unread') {
                        setTimeout(() => {
                            this.loadNotifications();
                        }, 500);
                    }
                    this._notificationsService.refreshUnreadCount();
                },
                error: (error) => {
                    console.error('Error marking as read:', error);
                }
            });
        }
    }

    /**
     * Delete notification
     */
    onDelete(notification: Notification): void
    {
        this.toggleRead(notification);
        this._notificationsService.deleteMany([notification.id]).subscribe();
    }

    /**
     * On notification click
     */
    onNotificationClick(notification: Notification): void
    {
        if(notification.link && /^(\/[a-zA-Z0-9]{1,})/.test(notification.link)) {
            if (!notification.read) this.toggleRead(notification);
            this._router.navigate([`${notification.link}`]);
            return;
        }


        this.toggleRead(notification);

        if (notification.data?.eventLink) {
            const link = notification.data.eventLink;
            if (/^(\/[a-zA-Z0-9]{1,})/.test(link)) {
                console.log('Redirecting to****:', link);

                // Internal route
                this._router.navigate([link]);
                this.closePanel();
            } else if (/^https?:\/\//.test(link)) {
                console.log('Redirecting to****---:', link);

                // External URL
                this._router.navigateByUrl(link);
                this.closePanel();
            }
            return;
        }
        if (notification.redirect_url)
        {
            if(/^(\/[a-zA-Z0-9]{1,})/.test(notification.redirect_url)) {
                this._router.navigate([`${notification.redirect_url}`]);
                this.closePanel();
                return;
            }

            if(/^https:\/\//.test(notification.redirect_url)) {
                console.log('Redirecting to:', notification.redirect_url);

                this._router.navigateByUrl(notification.redirect_url);
                this.closePanel();
            }
        }
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any
    {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void
    {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop     : true,
            backdropClass   : 'fuse-backdrop-on-mobile',
            scrollStrategy  : this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._notificationsOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX : 'start',
                        originY : 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX : 'start',
                        originY : 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX : 'end',
                        originY : 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX : 'end',
                        originY : 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() =>
        {
            this._overlayRef.detach();
        });
    }
    /**
     * Get initials from first and last name
     */
    getInitials(firstName: string, lastName: string): string {
        if (!firstName && !lastName) {
            return '';
        }
        const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
        const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
        return `${firstInitial}${lastInitial}`;
    }

}
